<template>
    <v-card color="surface">
        <v-card-title>
            Create Sales Schedule
        </v-card-title>
        <v-card-text class="mt-4">
            <v-form
                ref="form">
                <v-row>
                    <v-col cols="6">
                        <DatePicker
                            label="Date"
                            v-model="form.start_date"
                            outlined
                            :rules="[isRequired]"
                            dense
                            hide-details="auto"
                            />
                    </v-col>
                    <v-col cols="6">
                        <v-select
                            label="Priority"
                            outlined
                            dense
                            hide-details="auto"
                            :items="priorities"
                            item-text="name"
                            item-value="id"
                            :loading="prioritiesLoading"
                            v-model="form.priority_id"
                        ></v-select>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" md="6">
                        <v-autocomplete
                            label="Customer"
                            v-model="form.customer_id"
                            :items="customers"
                            item-text="display_name"
                            item-value="id"
                            :rules="[isRequired]"
                            outlined
                            hide-details="auto"
                            dense
                            @update:search-input="searchCustomers"
                            :loading="customersLoading"
                            />
                    </v-col>
                    <v-col cols="12" md="3">
                        <v-autocomplete
                            label="Site"
                            outlined
                            dense
                            :items="sites"
                            hide-details="auto"
                            :rules="[isRequired]"
                            item-text="name"
                            item-value="id"
                            v-model="form.site_id"
                            :loading="sitesLoading"
                            />
                    </v-col>
                    <v-col cols="12" md="3" v-if="form.customer_id">
                        <v-btn block depressed outlined @click="handleOpenCustomerCenter">
                            center
                        </v-btn>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="6">
                        <v-autocomplete
                            label="Item"
                            v-model="form.item_id"
                            :rules="[isRequired]"
                            outlined
                            dense
                            hide-details="auto"
                            :items="items"
                            item-text="name"
                            item-value="id"
                            @update:search-input="searchItems"
                            :loading="itemsLoading"
                            >
                        </v-autocomplete>
                    </v-col>
                    <v-col cols="3">
                        <v-text-field
                            label="Quantity"
                            v-model="form.quantity"
                            :rules="[isRequired, isNumeric]"
                            outlined
                            dense
                            hide-details="auto">
                        </v-text-field>
                    </v-col>
                    <v-col cols="3">
                        <v-select
                            label="Measure Unit"
                            v-model="form.measure_unit_id"
                            :rules="[isRequired]"
                            outlined
                            dense
                            :items="measureUnits"
                            :loading="measureUnitsLoading"
                            item-text="name"
                            item-value="id"
                            hide-details="auto"
                            >
                        </v-select>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" md="6">
                        <v-select
                            label="Floor"
                            v-model="form.floor"
                            :items="floors"
                            outlined
                            dense
                            hide-details="auto"
                            >
                        </v-select>
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-autocomplete
                            label="Cast Type"
                            v-model="form.cast_type_id"
                            outlined
                            dense
                            item-text="name"
                            item-value="id"
                            :items="castTypes"
                            :loading="castTypesLoading"
                            hide-details="auto"
                            />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" md="">
                        <v-autocomplete
                            label="Batch Plant"
                            v-model="form.plant_id"
                            outlined
                            dense
                            hide-details="auto"
                            :items="plants"
                            item-text="name"
                            item-value="id"
                            :loading="plantsLoading"
                        >
                        </v-autocomplete>
                    </v-col>
                    <v-col cols="12" sm="12" md="6">
                        <v-select
                            label="Cubes"
                            :items="cubes"
                            v-model="form.cubes"
                            outlined
                            dense
                            hide-details="auto">
                        </v-select>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <v-textarea
                            outlined
                            v-model="form.remark"
                            label="Remarks"
                            hide-details>
                        </v-textarea>
                    </v-col>
                </v-row>
            </v-form>
        </v-card-text>
        <v-card-actions>
            <v-btn depressed color="primary" @click="handleSubmit" :loading="loading">submit</v-btn>
            <v-btn text @click="$emit('cancel')">close</v-btn>
        </v-card-actions>
        <PAlert v-model="alert.display" v-bind="alert"/>
        <CustomerDisplayDialog ref="customerDisplayDialog"/>
    </v-card>
</template>

<script>
import { isRequired, isNumeric } from './../../utils/validations';

import scheduleService from './../../services/scheduling/schedule';
import customerService from '../../services/scheduling/customer';
import siteService from '../../services/scheduling/site';
import itemService from '../../services/scheduling/item';
import castTypeService from './../../services/scheduling/castType';
import priorityService from '../../services/scheduling/priority';
import plantService from '../../services/scheduling/plant';

import DatePicker from './../../components/common/DatePicker.vue';
import PAlert from './../common/PAlert';
import { v4 as uuid } from "uuid";
import CustomerDisplayDialog from './CustomerDisplayDialog.vue';

export default {
    components: {
        DatePicker,
        PAlert,
        CustomerDisplayDialog
    },
    props: {
        date: String
    },
    data() {
        return {
            idempotentKey: null,
            form: {
                id: null,
                customer_id: null,
                priority_id: 2,
                start_date: null,
                start_time: "",
                end_date: null,
                end_time: "",
                item_id: null,
                quantity: null,
                measure_unit_id: null,
                remark: null,
                cast_type_id: null,
                cubes: 0,
                floor: null
            },
            isRequired,
            isNumeric,
            loading: false,
            alert: {
                display: false,
                title: null,
                content: null,
                callback: null
            },
            cubes: [
                { text: "No Lab", value: 0 },
                { text: "1 Set (6 Cubes)", value: 6 },
                { text: "2 Sets (12 Cubes)", value: 12 },
                { text: "3 Sets (18 Cubes)", value: 18 },
                { text: "4 Sets (24 Cubes)", value: 24 },
                { text: "5 Sets (30 Cubes)", value: 30 },
                { text: "6 Sets (36 Cubes)", value: 36 },
            ],
            floors: [
                { text: "Basement", value: -1 },
                { text: "Ground Floor", value: 0 },
                { text: "1st Floor", value: 1 },
                { text: "2nd Floor", value: 2 },
                { text: "3rd Floor", value: 3 },
                { text: "4th Floor", value: 4 },
                { text: "5th Floor", value: 5 },
                { text: "6th Floor", value: 6 },
                { text: "7th Floor", value: 7 },
                { text: "8th Floor", value: 8 },
                { text: "9th Floor", value: 9 },
                { text: "10th Floor", value: 10 },
                { text: "11th Floor", value: 11 },
                { text: "12th Floor", value: 12 },
                { text: "13th Floor", value: 13 },
                { text: "14th Floor", value: 14 },
                { text: "15th Floor", value: 15 },
                { text: "16th Floor", value: 16 },
                { text: "17th Floor", value: 17 },
                { text: "18th Floor", value: 18 },
                { text: "19th Floor", value: 19 },
                { text: "20th Floor", value: 20 },
            ],
            customers: [],
            customersLoading: false,
            sites: [],
            sitesLoading: false,
            items: [],
            itemsLoading: false,
            measureUnits: [],
            measureUnitsLoading: false,
            castTypes: [],
            castTypesLoading: false,
            priorities: [],
            prioritiesLoading: false,
            plants: [],
            plantsLoading: false,
        }
    },
    computed: {

    },
    watch: {
        "form.customer_id": {
            handler() {
                this.form.site_id = null;
                this.searchSites();
            }
        },
        "form.item_id": {
            handler() {
                this.form.measure_unit_id = null;
                this.fetchItemMeasureUnits();
            }
        }
    },
    methods: {
        implodeDatetime(date, time) {
            return `${date} ${time}`
        },
        handleSubmit() {
            this.alert = {
                title: "Please Confirm",
                content: "Are you sure you want to save?",
                callback: this.submit,
                display: true
            }
        },
        async submit() {
            if(!this.$refs.form.validate()) return;

            this.loading = true;

            this.form.end_date = this.form.start_date;
            let data = { 
                ...this.form,
                idempotentKey: this.idempotentKey,
                start_at: this.implodeDatetime(this.form.start_date, this.form.start_time),
                end_at: this.implodeDatetime(this.form.end_date, this.form.end_time),
            }

            scheduleService.create(data)
                .then(resp => {
                    this.$emit("created", resp.data);
                })
                .finally(() => this.loading = false)
        },
        searchCustomers(query) {
            this.customersLoading = true;
            customerService.search(query).then(res => {
                this.customers = [...this.customers, ...res.data];
            }).finally(() => {
                this.customersLoading = false;
            })
        },
        searchSites() {
            this.sites = [];
            this.sitesLoading = true;
            siteService.all(this.form.customer_id).then(res => {
                this.sites = res.data
            }).finally(() => {
                this.sitesLoading = false;
            });
        },
        searchItems(query) {
            this.itemsLoading = true;
            itemService.search(query).then(res => {
                this.items = [...this.items, ...res.data];
            }).finally(() => {
                this.itemsLoading = false;
            })
        },
        fetchItemMeasureUnits() {
            this.measureUnitsLoading = true;
            itemService.measureUnits(this.form.item_id).then(res => {
                this.measureUnits = res.data;
            }).finally(() => {
                this.measureUnitsLoading = false;
            })
        },
        fetchCastTypes() {
            this.castTypesLoading = true;
            castTypeService.all().then(res => {
                this.castTypes = res.data;
            }).finally(() => {
                this.castTypesLoading = false;
            })
        },
        fetchPriorities() {
            this.prioritiesLoading = true;
            priorityService.all().then(res => {
                this.priorities = res.data;
            }).finally(() => {
                this.prioritiesLoading = false;
            })
        },
        fetchPlants() {
            this.plantsLoading = true;
            plantService.all().then(res => {
                this.plants = res.data;
            }).finally(() => {
                this.plantsLoading = false;
            })
        },
        handleOpenCustomerCenter() {
            this.$refs.customerDisplayDialog.open({ id: this.form.customer_id });
        }
    },
    created() {
        this.idempotentKey = uuid();
        this.form.start_date = this.date;
        this.fetchCastTypes();
        this.fetchPriorities();
        this.fetchPlants();
    }
}
</script>

<style>

</style>